import { memo, useContext, useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import Heading from 'LuxKit/Typography/Heading'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import FormatCurrency from 'components/Common/FormatCurrency/FormatCurrency'
import BodyText from 'LuxKit/Typography/BodyText'
import Image from 'components/Common/Image/Image'
import CarHireCancellationPolicyText from '../CarHireCancellationPolicyText'
import ModalContext from 'contexts/ModalContext'
import noop from 'lib/function/noop'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import CarHireInclusionsAndDetailsModal from './CarHireInclusionsAndDetailsModal'
import CarHireOfferTileLocation from './CarHireOfferTileLocation'
import CarHireOfferTileVehicleDetailsCondensed from './CarHireOfferTileVehicleDetailsCondensed'
import CarHirePriceDetailsModal from './CarHirePriceDetailsModal'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import PriceRowPriceDetails from 'LuxKit/PricePoints/PriceRowPriceDetails'
import CarHireOfferTileInclusions from './CarHireOfferTileInclusions'
import { isCarHireInsuranceAvailable } from 'selectors/carHireSelectors'
import CarHireInsuranceText from '../CarHireInsuranceText'
import Tooltip from 'LuxKit/Tooltip'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import Pill from 'LuxKit/Pill'
import { connect } from 'react-redux'
import { isPayFullyOnArrivalAvailable, isPayPartiallyAvailable } from 'lib/carHire/carHireUtils'
import useCarHireVehicleTypeLabels from 'hooks/CarHire/useCarHireVehicleTypeLabels'
import AgentHubAdditionalCommissionLabel from 'agentHub/components/AgentHubAdditionalCommissionLabel'
import { FormattedMessage, useIntl } from 'react-intl'

const Tile = styled.div`
  padding: ${rem(20)};
  width: 100%;
  display: grid;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  gap: ${rem(8)};
  grid-template-columns: 1fr;
`

const TileContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(12)};

  ${mediaQueryUp.desktop} {
    gap: ${rem(16)};
  }
`

const CarImage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  filter: brightness(1.25);
  height: 100%;
`

interface Props {
  offer: App.CarHireOffer
  className?: string
  onReserve?: (offer: App.CarHireOffer) => void
  priceFormat?: 'per-day' | 'total'
  pickUpDate: string
  dropOffDate: string
  pickUpTime: string
  dropOffTime: string
  isCarHireInsuranceAvailable: boolean
}

function CarHireOfferTileCondensed(props: Props) {
  const {
    offer,
    className,
    onReserve = noop,
    priceFormat = 'per-day',
    pickUpDate,
    dropOffDate,
    pickUpTime,
    dropOffTime,
    isCarHireInsuranceAvailable,
  } = props
  const intl = useIntl()
  const vehicleTypeLabels = useCarHireVehicleTypeLabels()

  const isPayOnArrivalAvailable = isPayFullyOnArrivalAvailable(offer)
  const hasPayAtPickupAmount = isPayOnArrivalAvailable || isPayPartiallyAvailable(offer)

  const allInclusionsTitle = intl.formatMessage({
    id: 'bZ-9dpbVXq-hc-jMGQ3m',
    defaultMessage: 'All inclusions & details',
    description: 'Title for car hire inclusions and details modal opened from condensed tile',
  })

  const guaranteedModelLabel = intl.formatMessage({
    id: 'IjbX-BUtig42UcxFWfX2',
    defaultMessage: 'Guaranteed model',
    description: 'Status label indicating the exact car hire vehicle model is guaranteed in condensed tile',
  })

  const orSimilarLabel = intl.formatMessage({
    id: 'g-8dfvH5ZZaQ242WMYgc',
    defaultMessage: 'or similar',
    description: 'Label indicating car hire vehicle model may be substituted with a similar one in condensed tile',
  })

  const payAtPickupTooltip = intl.formatMessage({
    id: 'k1k6eVOuuu9GkPo25V3A',
    defaultMessage: 'Reserve a car today with $0 and pay the total amount at pick-up.',
    description: 'Tooltip explaining pay at pick-up option for car hire condensed tile',
  })

  const payAtPickupLabel = intl.formatMessage({
    id: 'See46BtBPeLqCh53xFGO',
    defaultMessage: 'Pay at pick-up',
    description: 'Pill label for pay at pick-up option on car hire condensed tile',
  })

  const bookNowLabel = intl.formatMessage({
    id: 'eNAZBOKqwyO7XvEA5Abu',
    defaultMessage: 'Book now',
    description: 'Button text to book car hire offer from condensed tile',
  })

  const showModal = useContext(ModalContext)
  const onViewInclusions = () => {
    showModal(
      <CarHireInclusionsAndDetailsModal
        offer={offer}
        pickUpDate={pickUpDate}
        dropOffDate={dropOffDate}
        pickUpTime={pickUpTime}
        dropOffTime={dropOffTime}
        showInclusions
        title={allInclusionsTitle}
      />)
  }

  const onViewPriceDetails = () => {
    showModal(<CarHirePriceDetailsModal offer={offer} pickUpDate={pickUpDate} onReserve={onReserve} />)
  }

  const desktopImage = useMemo(() => ({
    ...offer.vehicle.image,
    // modify the image URL to utilise car trawlers image options for a better image
    url: `${offer.vehicle.image.url}?w=216&dpr=2`,
  }), [offer.vehicle.image])

  const mobileImage = useMemo(() => ({
    ...offer.vehicle.image,
    // modify the image URL to utilise car trawlers image options for a better image
    // 'secondary' faces left, 'primary' faces right
    url: `${offer.vehicle.image.url?.replace('primary', 'secondary')}?w=108&dpr=2`,
  }), [offer.vehicle.image])

  return <Tile className={className}>
    {/* desktop */}
    <CSSBreakpoint min="desktop">
      <Group direction="vertical" gap={8}>
        <Group direction="horizontal" horizontalAlign="space-between">
          <Group direction="horizontal" fullWidth horizontalAlign="end">
            <Image width={80} image={offer.vendor.image} />
          </Group>
        </Group>
        <CarImage>
          <Image width={216} height={121.5} image={desktopImage} />
        </CarImage>
        <LabelGroup>
          <StructureLabel variant="standard">{vehicleTypeLabels[offer.vehicle.type] ?? offer.vehicle.type}</StructureLabel>
          {offer.guarenteedModel && <StatusLabel variant="confirmation">{guaranteedModelLabel}</StatusLabel>}
        </LabelGroup>
      </Group>
    </CSSBreakpoint>
    {/* mobile */}
    <CSSBreakpoint max="tablet">
      <Group direction="horizontal" horizontalAlign="space-between">
        <LabelGroup>
          <StructureLabel variant="standard">{vehicleTypeLabels[offer.vehicle.type] ?? offer.vehicle.type}</StructureLabel>
          {offer.guarenteedModel && <StatusLabel variant="confirmation">{guaranteedModelLabel}</StatusLabel>}
        </LabelGroup>
        <Group direction="horizontal" gap={8} verticalAlign="center">
          <Image width={64} image={offer.vendor.image} />
        </Group>
      </Group>
    </CSSBreakpoint>

    <TileContent>
      <VerticalSpacer gap={12}>
        <VerticalSpacer gap={8}>
          <div>
            <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4 }}>
              <Heading as="span" variant="heading3">{offer.vehicle.model}</Heading>
              {!offer.guarenteedModel && <BodyText wrap="no-wrap" as="span" variant="medium" colour="neutral-three">{orSimilarLabel}</BodyText>}
            </span>
          </div>
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={8}>
            <VerticalSpacer gap={4}>
              <CarHireOfferTileVehicleDetailsCondensed offer={offer} showMileage={false} />
              <CarHireOfferTileLocation offer={offer} />
            </VerticalSpacer>
            <CSSBreakpoint max="tablet">
              <CarImage>
                <Image width={108} image={mobileImage} />
              </CarImage>
            </CSSBreakpoint>
          </Group>
        </VerticalSpacer>
        <CarHireOfferTileInclusions offer={offer} onViewInclusions={onViewInclusions} />
      </VerticalSpacer>

      <VerticalSpacer gap={0}>
        {offer.cancellationPolicy && <CarHireCancellationPolicyText policy={offer.cancellationPolicy} />}
        {isCarHireInsuranceAvailable &&
          <CarHireInsuranceText
            offer={offer}
            isEnhancedProtectionSelected={false}
            pickUpDate={pickUpDate || ''}
            dropOffDate={dropOffDate || ''}
          />}
        <AgentHubAdditionalCommissionLabel offerId={offer?.id} />
      </VerticalSpacer>

      <Group
        direction="horizontal"
        horizontalAlign="space-between"
        verticalAlign="end"
        gap={8}
      >
        <VerticalSpacer gap={8}>
          {isPayOnArrivalAvailable && <Tooltip description={payAtPickupTooltip}>
            <Pill kind="tertiary" variant="primary" startIcon={<LineCheckCircleIcon />}>{payAtPickupLabel}</Pill>
          </Tooltip>}
          <div>
            {priceFormat === 'per-day' && <>
              <PriceRowPrice
                size="L"
                price={offer.pricePerDay}
                saleUnit="day"
              />
              {offer.value > offer.price && <BodyText variant="medium" colour="neutral-two" strikeThrough>
                <FormattedMessage
                  id="LXObZQ6NSP6lz1meab4k"
                  defaultMessage="Was {price}"
                  description="Struck-through original price for car hire condensed offer tile"
                  values={{ price: <FormatCurrency value={offer.valuePerDay} /> }}
                />
              </BodyText>}
              {offer.duration > 1 && <PriceRowCaption>
                <FormattedMessage
                  id="obdRItOAJHse1O-xCN0q"
                  defaultMessage="Total {price} for {count, plural, one {# day} other {# days}}"
                  description="Total price and duration for car hire condensed offer per-day format"
                  values={{ price: <FormatCurrency value={offer.price} />, count: offer.duration }}
                />
              </PriceRowCaption>}
            </>}
            {priceFormat === 'total' && <>
              {offer.duration > 1 && <PriceRowPriceCaption>
                {intl.formatMessage(
                  {
                    id: 'Dgc5dUSV4izDxnPW3FjC',
                    defaultMessage: 'For {count, plural, one {# day} other {# days}}',
                    description: 'Duration caption for car hire condensed offer total price format',
                  },
                  { count: offer.duration },
                )}
              </PriceRowPriceCaption>}
              <PriceRowPrice
                size="L"
                price={offer.price}
                saleUnit="total"
              />
              {offer.duration > 1 && <PriceRowCaption>
                <FormattedMessage
                  id="VQ_Mu8qbAsCL3pRP8jUh"
                  defaultMessage="{price}/day"
                  description="Price per day caption for car hire condensed offer total price format"
                  values={{ price: <FormatCurrency value={offer.pricePerDay} /> }}
                />
              </PriceRowCaption>}
            </>}
            <PriceRowTaxesAndFees />
            {hasPayAtPickupAmount && <PriceRowPriceDetails size="M" onClick={onViewPriceDetails} />}
          </div>
        </VerticalSpacer>
        <TextButton kind="primary" onClick={() => onReserve(offer)}>
          {bookNowLabel}
        </TextButton>
      </Group>
    </TileContent>
  </Tile>
}

function mapStateToProps(state: App.State) {
  return {
    isCarHireInsuranceAvailable: isCarHireInsuranceAvailable(state),
  }
}

export default connect(mapStateToProps)(memo(CarHireOfferTileCondensed))
